// src/CancelamentoModal.js

import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogActions, DialogContent, DialogTitle,
  Button, Typography, Divider, Paper, Grid, IconButton, Tooltip, Box
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PrintIcon from '@mui/icons-material/Print';
import dayjs from 'dayjs';
import useReservasStore from './store/reservasStore';

const formatarMoeda = (valor) => {
    return (valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const CancelamentoModal = ({ open, reserva, onClose, onConfirm }) => {
  const { configuracoes } = useReservasStore();
  const [calculo, setCalculo] = useState(null);
  const printableRef = useRef(null);

  useEffect(() => {
    if (reserva && configuracoes) {
      const hoje = dayjs();
      const dataCheckin = dayjs(reserva.dataCheckin);
      const diasParaCheckin = dataCheckin.diff(hoje, 'day');

      const valorPago = (reserva.adiantamento?.valor || 0) + (reserva.pagamentoRestante?.valor || 0);
      const valorDiaria = reserva.numDiarias > 0 ? reserva.valorTotal / reserva.numDiarias : 0;

      let multa = 0;
      let tipoMulta = '';
      let valorFinal = 0;
      let tipoResultado = '';
      
      const diasAntecedencia = configuracoes.diasAntecedenciaCancelamento || 15;
      const percentualMulta = (configuracoes.percentualMultaAntecedencia || 20) / 100;

      if (diasParaCheckin >= diasAntecedencia) {
        multa = valorPago * percentualMulta;
        tipoMulta = `Multa de ${configuracoes.percentualMultaAntecedencia}% sobre o valor pago (${formatarMoeda(valorPago)})`;
        valorFinal = valorPago - multa;
        tipoResultado = 'Valor a Devolver';
      } else {
        multa = valorDiaria;
        tipoMulta = `Multa correspondente a 1 diária (${formatarMoeda(valorDiaria)})`;
        valorFinal = valorPago - multa;
        tipoResultado = 'Crédito para Futura Estadia';
      }

      setCalculo({
        diasParaCheckin,
        valorPago,
        multa,
        tipoMulta,
        valorFinal: Math.max(0, valorFinal),
        tipoResultado,
      });
    }
  }, [reserva, configuracoes]);

  const handlePrint = () => {
    window.print();
  };

  if (!calculo) return null;

  return (
    <>
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            .printable-area, .printable-area * {
              visibility: visible;
            }
            .printable-area {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
            }
            .no-print {
              display: none;
            }
          }
        `}
      </style>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningAmberIcon color="warning" />
            Confirmar Cancelamento
          </Box>
          <Tooltip title="Imprimir Comprovativo">
            <IconButton onClick={handlePrint} className="no-print">
              <PrintIcon />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent dividers ref={printableRef} className="printable-area">
          <Typography variant="body1" sx={{ mb: 2 }}>
            A reserva de <strong>{reserva.hospedeNome}</strong> está sendo cancelada a <strong>{calculo.diasParaCheckin} dias</strong> da data de check-in.
          </Typography>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Grid container spacing={1}>
              <Grid item xs={6}><Typography>Valor Total Pago:</Typography></Grid>
              <Grid item xs={6}><Typography align="right" fontWeight="bold">{formatarMoeda(calculo.valorPago)}</Typography></Grid>

              <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>

              <Grid item xs={6}><Typography color="error">Cálculo da Multa:</Typography></Grid>
              <Grid item xs={6}><Typography align="right" color="error">-{formatarMoeda(calculo.multa)}</Typography></Grid>
              <Grid item xs={12}><Typography variant="caption" color="text.secondary">{calculo.tipoMulta}</Typography></Grid>
              
              <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>

              <Grid item xs={6}><Typography variant="h6">{calculo.tipoResultado}:</Typography></Grid>
              <Grid item xs={6}><Typography variant="h6" align="right" color="primary.main">{formatarMoeda(calculo.valorFinal)}</Typography></Grid>
            </Grid>
          </Paper>
          <Typography variant="caption" sx={{ mt: 2, display: 'block' }} className="no-print">
            Ao confirmar, o status da reserva será alterado para "Cancelada" e os dias no mapa de ocupação serão liberados.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: '16px 24px' }} className="no-print">
          <Button onClick={onClose} variant="outlined">Voltar</Button>
          <Button onClick={() => onConfirm(calculo)} variant="contained" color="error">
            Confirmar Cancelamento
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CancelamentoModal;

